import {
  Badge,
  buildDataTableColumns,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  DataTableBody,
  DataTableHeader,
  DataTablePagination,
  Label,
  SectionTitle,
  Table,
} from "@bleu.builders/ui";
import { MessageCircleIcon } from "lucide-react";
import React from "react";

import { Link } from "#/components/Link";
import { useUser } from "#/contexts/UserContext";
import { formatDate } from "#/lib/formatDate";
import { replaceParams, UserPagePath } from "#/lib/pagesHelper";

const SUBSCRIPTION_STATUS_MAP = {
  active: "Ativa",
  canceled: "Cancelada",
  incomplete: "Incompleta",
  incomplete_expired: "Incompleta Expirada",
  past_due: "Atrasada",
  trialing: "Em Teste",
  unpaid: "Não Paga",
  paused: "Pausada",
};
import { useSWRDataTableWithRedirect } from "#/utils/useSWRDataTableWithRedirect";

import { AddResourceCardWrapper } from "../(components)/AddResourceModal";
import { FieldsLoaderData } from "../(components)/types";

const CURRENT_PLAN = ({ user }) => {
  return [
    {
      name: "Plano",
      element: user.plan?.name,
    },
    {
      name: "Status",
      element: (
        <Badge>{SUBSCRIPTION_STATUS_MAP[user.subscription.status]}</Badge>
      ),
    },
    {
      name: "Ciclo atual começa em",
      element: user.subscription.current_period_start
        ? formatDate(user.subscription.current_period_start)
        : "",
    },
    {
      name: "Ciclo atual termina em",
      element: user.subscription.current_period_end
        ? formatDate(user.subscription.current_period_end)
        : "",
    },
  ];
};

const InvoicesTable = () => {
  const { user } = useUser();

  const path = replaceParams(UserPagePath.ORGANIZATION_INVOICES, {
    organization_id: user.organization.id,
  });

  const { data, error, tableState, setTableState } =
    useSWRDataTableWithRedirect(path);

  return (
    <div className="max-w-7xl w-full">
      <div className="flex items-center justify-between space-y-2">
        <div>
          <SectionTitle className="pt-0">Cobranças</SectionTitle>
          <p className="text-muted-foreground">
            Aqui está uma lista de Cobranças.
          </p>
        </div>
      </div>

      <DataTable
        data={data}
        error={error}
        tableState={tableState}
        setTableState={setTableState}
        setSelectedData={null}
        setQueryToParams={false}
        // @ts-expect-error TS(2339) FIXME: Property 'buildTableColumns' does not exist on type '{}'.
        buildTableColumns={(tableColumns, tableFilters) =>
          buildDataTableColumns(tableColumns, tableFilters, [])
        }
      >
        <div className="border-2 rounded-lg mb-2">
          <Table>
            <DataTableHeader />
            <DataTableBody hasDetails={false} />
          </Table>
        </div>
        <DataTablePagination />
      </DataTable>
    </div>
  );
};

function CurrentPlanCard({ user }) {
  return (
    <Card className="max-w-lg rounded-lg bg-card text-card-foreground shadow-sm border-2">
      <CardHeader>
        {user.subscription.ends_at && (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mb-4 rounded relative flex items-center justify-center gap-1"
            role="alert"
          >
            <strong className="font-bold">Aviso:</strong>
            <span className="block sm:inline">
              Sua assinatura se encerra dia{" "}
              {formatDate(user.subscription.ends_at)}
            </span>
          </div>
        )}
        <CardTitle className="text-xl font-semibold leading-none tracking-tight pl-0">
          Assinatura
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground pl-0">
          Aqui está um resumo da sua assinatura atual.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 mx-4">
        <div className="grid grid-cols-2 gap-2">
          {CURRENT_PLAN({ user }).map((item, index) => {
            return (
              item.element && (
                <div key={index} className="space-y-1.5">
                  <Label htmlFor={item.name} className="font-bold">
                    {item.name}
                  </Label>
                  <div>{item.element}</div>
                </div>
              )
            );
          })}
        </div>
        <Link
          to="https://wa.me/554891130660?text=Ol%C3%A1%2C+sou+assinante+e+gostaria+de+esclarecer+uma+d%C3%BAvida."
          target="_blank"
        >
          <Button size="sm" variant="outline" className="mt-3">
            <MessageCircleIcon className="mr-1.5 size-4" />
            Converse com o Suporte para gerenciar assinatura
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

function CreateSubscription() {
  return (
    <Card className="max-w-lg rounded-lg bg-card text-card-foreground shadow-sm border-2">
      <CardHeader>
        <CardTitle className="text-xl font-semibold leading-none tracking-tight pl-0">
          Assinatura
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground pl-0">
          Voce não tem uma assinatura ativa.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 mx-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Voce precisa de uma assinatura para acessar os conteúdos
        </p>
        <Link
          to="https://wa.me/554891130660?text=Ol%C3%A1%2C+sou+assinante+e+gostaria+de+esclarecer+uma+d%C3%BAvida."
          target="_blank"
        >
          <Button size="sm" variant="outline">
            <MessageCircleIcon className="mr-1.5 size-4" />
            Converse com o Suporte
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

function AdminSubscription() {
  return (
    <Card className="max-w-lg rounded-lg bg-card text-card-foreground shadow-sm border-2">
      <CardHeader>
        <CardTitle className="text-xl font-semibold leading-none tracking-tight pl-0">
          Assinatura
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground pl-0">
          Voce é um administrador e pode criar assinaturas no painel de admin.
          Basta procurar pelo usuário, clicar em "Detalhes do Usuário" e depois
          "Assinatura".
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

export default function Subscription() {
  const { user } = useUser();

  return (
    <div className="size-full flex flex-col">
      <div className="my-5">
        {user?.subscription ? (
          <CurrentPlanCard user={user} />
        ) : user.is_admin ? (
          <AdminSubscription />
        ) : (
          <CreateSubscription />
        )}
      </div>
      <div className="mb-5">
        {user.has_invoices ? <InvoicesTable /> : <></>}
      </div>
    </div>
  );
}
